import React, { useState, useEffect, useRef } from 'react';
import { db } from './lib/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// ─── Search Bar ──────────────────────────────────────────────────────────────
function SearchBar({ value, onChange }) {
  return (
    <div className="relative flex-1 max-w-xl">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
      </svg>
      <input
        type="text"
        placeholder="Search by tag, mood, description, subject…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-600 text-white text-sm pl-9 pr-8 py-2 outline-none transition-colors"
        style={{ fontFamily: 'inherit' }}
      />
      {value && (
        <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors text-xs">✕</button>
      )}
    </div>
  );
}

// ─── Clip Card ────────────────────────────────────────────────────────────────
function ClipCard({ clip, onSelect }) {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
    videoRef.current?.play().catch(() => {});
  };
  const handleMouseLeave = () => {
    setHovered(false);
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
  };

  const displayName = clip.fileName?.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
  const isReady = clip.status === 'ready';

  const statusStyle = {
    ready:      { color: '#fff',        bg: 'rgba(255,255,255,0.08)' },
    uploaded:   { color: '#facc15',     bg: 'rgba(250,204,21,0.08)' },
    processing: { color: '#38bdf8',     bg: 'rgba(56,189,248,0.08)' },
    error:      { color: '#f87171',     bg: 'rgba(248,113,113,0.08)' },
  }[clip.status] ?? { color: '#888', bg: 'transparent' };

  return (
    <div
      style={{ backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a', overflow: 'hidden', cursor: isReady ? 'pointer' : 'default', transition: 'border-color 0.2s', opacity: isReady ? 1 : 0.5 }}
      onMouseEnter={() => { isReady && handleMouseEnter(); }}
      onMouseLeave={() => { isReady && handleMouseLeave(); }}
      onMouseOver={(e) => { if (isReady) e.currentTarget.style.borderColor = '#333'; }}
      onMouseOut={(e) => { e.currentTarget.style.borderColor = '#1a1a1a'; }}
      onClick={() => isReady && clip.proxyUrl && onSelect(clip)}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '16/9', backgroundColor: '#000' }}>
        {clip.proxyUrl ? (
          <video ref={videoRef} src={clip.proxyUrl} muted loop playsInline preload="metadata"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '20px', height: '20px', border: '2px solid #333', borderTopColor: '#38bdf8', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* Play overlay */}
        {isReady && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', opacity: hovered ? 0 : 1, transition: 'opacity 0.2s' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" fill="white" viewBox="0 0 24 24" style={{ marginLeft: '2px' }}>
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}

        {/* Badges */}
        <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
          <span style={{ fontSize: '9px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', padding: '3px 8px', color: statusStyle.color, background: statusStyle.bg }}>
            {clip.status}
          </span>
        </div>
        {clip.mood && (
          <div style={{ position: 'absolute', bottom: '8px', left: '8px' }}>
            <span style={{ fontSize: '9px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', padding: '3px 8px', background: 'rgba(0,0,0,0.6)', color: '#888' }}>
              {clip.mood}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '14px' }}>
        <p style={{ color: '#e8e8e8', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '6px' }}>{displayName}</p>
        {clip.description && (
          <p style={{ color: '#555', fontSize: '0.75rem', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '8px' }}>
            {clip.description}
          </p>
        )}
        {clip.tags?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {clip.tags.slice(0, 4).map(tag => (
              <span key={tag} style={{ fontSize: '9px', fontFamily: 'monospace', padding: '2px 6px', background: '#1a1a1a', color: '#555' }}>{tag}</span>
            ))}
            {clip.tags.length > 4 && <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#444' }}>+{clip.tags.length - 4}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Video Modal ──────────────────────────────────────────────────────────────
function VideoModal({ clip, onClose }) {
  const videoRef = useRef(null);
  const displayName = clip.fileName?.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');

  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={onClose}
    >
      <div
        style={{ width: '100%', maxWidth: '900px', background: '#0a0a0a', border: '1px solid #1a1a1a', overflow: 'hidden', position: 'relative' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ aspectRatio: '16/9', background: '#000' }}>
          <video ref={videoRef} src={clip.proxyUrl} controls autoPlay style={{ width: '100%', height: '100%' }} />
        </div>
        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '24px', alignItems: 'start' }}>
          <div>
            <h2 style={{ color: '#e8e8e8', fontWeight: 700, fontSize: '1rem', marginBottom: '8px' }}>{displayName}</h2>
            {clip.description && <p style={{ color: '#555', fontSize: '0.8rem', lineHeight: 1.6, marginBottom: '12px' }}>{clip.description}</p>}
            {clip.tags?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {clip.tags.map(tag => (
                  <span key={tag} style={{ fontSize: '10px', fontFamily: 'monospace', padding: '3px 8px', background: '#1a1a1a', color: '#555', border: '1px solid #222' }}>{tag}</span>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#444', lineHeight: 2, whiteSpace: 'nowrap' }}>
            {[['Mood', clip.mood], ['Shot', clip.shotType], ['Status', clip.status]].map(([k, v]) => v ? (
              <div key={k}><span style={{ color: '#333', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{k}</span> <span style={{ color: '#666', marginLeft: '8px' }}>{v}</span></div>
            ) : null)}
          </div>
        </div>
        <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '12px', width: '28px', height: '28px', background: 'rgba(0,0,0,0.6)', border: '1px solid #333', color: '#666', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>
    </div>
  );
}

// ─── Main Portal ──────────────────────────────────────────────────────────────
export default function SlateVaultPortal() {
  const [clips, setClips]       = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [activeClip, setActiveClip] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'clips'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setClips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = clips.filter(clip => {
    if (!search.trim()) return true;
    const t = search.toLowerCase();
    return (
      clip.fileName?.toLowerCase().includes(t) ||
      clip.description?.toLowerCase().includes(t) ||
      clip.mood?.toLowerCase().includes(t) ||
      clip.shotType?.toLowerCase().includes(t) ||
      clip.tags?.some(x => x.toLowerCase().includes(t)) ||
      clip.subjects?.some(x => x.toLowerCase().includes(t))
    );
  });

  const ready   = filtered.filter(c => c.status === 'ready');
  const pending = filtered.filter(c => c.status !== 'ready');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#050505', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Toolbar */}
      <div style={{ borderBottom: '1px solid #111', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '16px', position: 'sticky', top: '41px', zIndex: 20, backgroundColor: 'rgba(5,5,5,0.97)', backdropFilter: 'blur(8px)' }}>
        <SearchBar value={search} onChange={setSearch} />
        <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#333', whiteSpace: 'nowrap' }}>
          {loading ? '—' : `${ready.length} clip${ready.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      <div style={{ padding: '32px 24px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#333', fontSize: '0.8rem', letterSpacing: '0.1em' }}>
            LOADING VAULT…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#333', fontSize: '0.8rem', letterSpacing: '0.1em' }}>
            {search ? `NO CLIPS MATCH "${search.toUpperCase()}"` : 'NO CLIPS YET — START THE WATCHER'}
          </div>
        ) : (
          <>
            {ready.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {ready.map(clip => <ClipCard key={clip.id} clip={clip} onSelect={setActiveClip} />)}
              </div>
            )}

            {pending.length > 0 && (
              <div style={{ marginTop: '40px' }}>
                <p style={{ fontSize: '10px', fontFamily: 'monospace', letterSpacing: '0.3em', color: '#333', textTransform: 'uppercase', marginBottom: '16px' }}>
                  Processing ({pending.length})
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                  {pending.map(clip => <ClipCard key={clip.id} clip={clip} onSelect={setActiveClip} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {activeClip && <VideoModal clip={activeClip} onClose={() => setActiveClip(null)} />}
    </div>
  );
}
